import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { DEMO_INITIAL_STATE, DEMO_SCENES } from '../data/demoScript';

const STORAGE_KEY = 'hst-demo-progress';

/**
 * NOTE on storage choice (31/05/2026 — code review answer):
 *   This persists the visitor's local progress in the unauthenticated
 *   demo (scene index, mock figures, pause state). NO PII, NO secret, NO
 *   auth token — just a UX continuity bookmark so the visitor can refresh
 *   the page without losing their place. localStorage is the right tool:
 *   sessionStorage would wipe progress on tab close (the exact opposite
 *   of the intent), and encryption would add zero security since the
 *   payload is entirely public mock data. Same reasoning as
 *   `useBankruptcyReporter` / `InternationalSwitchModal`.
 */
function loadProgress() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveProgress(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      sceneIdx: state.sceneIdx,
      mockState: state.mockState,
      paused: state.paused,
      completed: state.completed,
    }));
  } catch (err) {
    console.warn('[demoPlayer] failed to save progress:', err.message);
  }
}

const INITIAL = {
  sceneIdx: 0,
  mockState: DEMO_INITIAL_STATE,
  paused: false,
  completed: false,
};

function reducer(state, action) {
  switch (action.type) {
    case 'advance': {
      const next = state.sceneIdx + 1;
      if (next >= DEMO_SCENES.length) return { ...state, sceneIdx: state.sceneIdx, completed: true };
      const scene = DEMO_SCENES[next];
      return {
        ...state,
        sceneIdx: next,
        mockState: scene.stateUpdate ? { ...state.mockState, ...scene.stateUpdate } : state.mockState,
        completed: scene.kind === 'contact',
      };
    }
    case 'reset':
      return { ...INITIAL };
    case 'pause':
      return { ...state, paused: true };
    case 'resume':
      return { ...state, paused: false };
    case 'hydrate':
      return { ...state, ...action.payload };
    default:
      return state;
  }
}

/**
 * Demo player hook — drives the scripted scene-by-scene flow.
 * - Auto-advances every `scene.duration` ms when not paused.
 * - Persists progress in localStorage so a refresh resumes mid-scene.
 * - Exposes a `restart()` action so the user can replay from scratch.
 */
export default function useDemoPlayer() {
  const [state, dispatch] = useReducer(reducer, INITIAL);
  const hydrated = useRef(false);

  // Hydrate from localStorage on mount.
  useEffect(() => {
    if (hydrated.current) return;
    hydrated.current = true;
    const saved = loadProgress();
    if (saved && Number.isInteger(saved.sceneIdx) && saved.sceneIdx < DEMO_SCENES.length) {
      dispatch({ type: 'hydrate', payload: saved });
    }
  }, []);

  // Persist on every change after hydration.
  useEffect(() => {
    if (!hydrated.current) return;
    saveProgress(state);
  }, [state]);

  // Auto-advance timer.
  useEffect(() => {
    if (state.paused) return undefined;
    const scene = DEMO_SCENES[state.sceneIdx];
    if (!scene || !scene.duration || state.completed) return undefined;
    const id = setTimeout(() => dispatch({ type: 'advance' }), scene.duration);
    return () => clearTimeout(id);
  }, [state.sceneIdx, state.paused, state.completed]);

  const scene = DEMO_SCENES[state.sceneIdx];

  const advance = useCallback(() => dispatch({ type: 'advance' }), []);
  const restart = useCallback(() => {
    try { localStorage.removeItem(STORAGE_KEY); } catch (err) { console.warn(err.message); }
    dispatch({ type: 'reset' });
  }, []);
  const pause = useCallback(() => dispatch({ type: 'pause' }), []);
  const resume = useCallback(() => dispatch({ type: 'resume' }), []);

  const progressPct = useMemo(() => Math.round((state.sceneIdx / Math.max(1, DEMO_SCENES.length - 1)) * 100), [state.sceneIdx]);

  return {
    scene,
    sceneIdx: state.sceneIdx,
    sceneCount: DEMO_SCENES.length,
    mockState: state.mockState,
    paused: state.paused,
    completed: state.completed,
    progressPct,
    advance,
    restart,
    pause,
    resume,
  };
}
